import { useMediaQuery, useTheme } from '@material-ui/core'
import { Cancel } from '@material-ui/icons'
import React from 'react'
import {
  BooleanInput,
  DateTimeInput,
  Edit,
  EditProps,
  FormDataConsumer,
  SaveButton,
  SelectInput,
  ShowButton,
  SimpleForm,
  TextField,
  TextInput,
  TitleProps,
  Toolbar,
  ToolbarProps,
  useTranslate,
} from 'react-admin'
import { formatTime } from '../../../core/common/date-time'
import { AccessDto } from '../../../core/dto/access.dto'
import { AccessActivePeriodStartingOptions } from '../../../core/enum/AccessActivePeriodStartingOptions'
import { DayOfWeek } from '../../../core/enum/DayOfWeek'
import { ResourceName } from '../../../core/ResourceName'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { Authority } from '../../../core/auth/Authority'
import { AccessBonusDto } from '../../../core/dto/access-bonus.dto'
import { AccessTicketPeriodOptions } from '../../../core/enum/AccessTicketPeriodOptions'
import { DeferLogicOptions } from '../../../core/enum/DeferLogicOptions'

const validateAccessEdit = (values: AccessDto) => {
  const deferIsActive = () => {
    if (values.allowReenter || values.multiplicity > 1) return true
    return false
  }
  const errors: { [n: string]: string } = {}
  if (!values.name) {
    errors.name = 'ra.validation.required'
  }
  if (!values.bonusId) {
    errors.bonusId = 'ra.validation.required'
  }
  if (!values.entranceId) {
    errors.entranceId = 'ra.validation.required'
  }
  if (Number.isNaN(Number(values.multiplicity)))
    errors.multiplicity = 'ra.validation.not_numeric'
  if (values.usePriority && Number.isNaN(Number(values.usePriority)))
    errors.usePriority = 'ra.validation.not_numeric'

  if (
    values.ticketUnlockOption &&
    values.ticketUnlockOption !== AccessTicketPeriodOptions.NONE
  ) {
    if (values.ticketUnlockOption === AccessTicketPeriodOptions.ONCE_A_DAY) {
      if (!values.unlockTime) errors.unlockTime = 'ra.validation.required'
    } else if (
      values.ticketUnlockOption === AccessTicketPeriodOptions.ONCE_A_WEEK
    ) {
      if (!values.unlockTime) errors.unlockTime = 'ra.validation.required'
      if (!values.unlockDayOfWeek)
        errors.unlockDayOfWeek = 'ra.validation.required'
    } else if (
      values.ticketUnlockOption === AccessTicketPeriodOptions.ONCE_A_MONTH
    ) {
      if (!values.unlockTime) errors.unlockTime = 'ra.validation.required'
      if (!values.unlockDayOfMonth)
        errors.unlockDayOfMonth = 'ra.validation.required'
      else if (Number.isNaN(Number(values.unlockDayOfMonth)))
        errors.unlockDayOfMonth = 'ra.validation.not_numeric'
      else if (
        Number(values.unlockDayOfMonth) > 31 ||
        Number(values.unlockDayOfMonth) < 1
      )
        errors.unlockDayOfMonth = 'ra.validation.not_day_of_the_month'
    } else if (
      values.ticketUnlockOption === AccessTicketPeriodOptions.AFTER_DELAY
    ) {
      if (!values.unlockDelay) errors.unlockDelay = 'ra.validation.required'
    }
  }
  if (
    deferIsActive() &&
    values.ticketDeferOption &&
    values.ticketDeferOption !== AccessTicketPeriodOptions.NONE
  ) {
    if (values.ticketDeferOption === AccessTicketPeriodOptions.ONCE_A_DAY) {
      if (!values.deferTime) errors.deferTime = 'ra.validation.required'
    } else if (
      values.ticketDeferOption === AccessTicketPeriodOptions.ONCE_A_WEEK
    ) {
      if (!values.deferTime) errors.deferTime = 'ra.validation.required'
      if (!values.deferDayOfWeek)
        errors.deferDayOfWeek = 'ra.validation.required'
    } else if (
      values.ticketDeferOption === AccessTicketPeriodOptions.ONCE_A_MONTH
    ) {
      if (!values.deferTime) errors.deferTime = 'ra.validation.required'
      if (!values.deferDayOfMonth)
        errors.deferDayOfMonth = 'ra.validation.required'
      else if (Number.isNaN(Number(values.deferDayOfMonth)))
        errors.deferDayOfMonth = 'ra.validation.not_numeric'
      else if (
        Number(values.deferDayOfMonth) > 31 ||
        Number(values.deferDayOfMonth) < 1
      )
        errors.deferDayOfMonth = 'ra.validation.not_day_of_the_month'
    } else if (
      values.ticketDeferOption === AccessTicketPeriodOptions.AFTER_DELAY
    ) {
      if (!values.deferDelay) errors.deferDelay = 'ra.validation.required'
    }
  }
  if (
    values.activePeriodStartingFrom !==
      AccessActivePeriodStartingOptions.NO_LIMIT &&
    !values.activePeriod
  ) {
    errors.activePeriod = 'ra.validation.required'
  }
  return errors
}

interface AccessTitleProps extends TitleProps {
  record?: AccessDto
}

const EditTitle = (data: AccessTitleProps) => {
  const { record } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.accesses.titles.edit')}: {record?.id}.{' '}
      {record?.name}{' '}
    </span>
  )
}

const EditToolbar = (props: ToolbarProps) => (
  <Toolbar {...props}>
    <SaveButton />
    <ShowButton icon={<Cancel />} label="const.cancel" />
  </Toolbar>
)

export const AccessEdit = (props: EditProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Edit {...props} hasShow={false} title={<EditTitle />}>
      <SimpleForm
        redirect="show"
        toolbar={<EditToolbar />}
        validate={validateAccessEdit}
      >
        <TextField source="id" label="ID" />
        <TextInput source="name" isRequired />
        {hasAuthority(Authority.VIEW_ACCESS_BONUSES) && (
          <FilteredReferenceInput
            source="bonusId"
            defaultValue={null}
            reference={ResourceName.ACCESS_BONUS}
            sort={{ field: 'name', order: 'ASC' }}
            perPage={smallScreen ? 5 : 15}
            isRequired
            filterSource="search"
            selectWithPaginationInputProps={{
              showFilter: true,
              optionText: (choice: AccessBonusDto) =>
                `${choice.name} (${choice.description})`,
            }}
          />
        )}
        {hasAuthority(Authority.VIEW_ENTRANCES) && (
          <FilteredReferenceInput
            source="entranceId"
            defaultValue={null}
            reference={ResourceName.ENTRANCES}
            sort={{ field: 'note', order: 'ASC' }}
            perPage={smallScreen ? 5 : 15}
            isRequired
            filterSource="idWithNote"
            selectWithPaginationInputProps={{
              optionText: 'note',
              showFilter: true,
            }}
          />
        )}
        <BooleanInput source="allowReenter" />
        <BooleanInput source="multipleExit" />
        <TextInput source="multiplicity" defaultValue={1} />
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            (formData?.allowReenter || formData?.multiplicity > 1) && (
              <SelectInput
                {...rest}
                source="ticketDeferOption"
                choices={Object.entries(AccessTicketPeriodOptions)
                  .filter(
                    ([value]) =>
                      value !== AccessTicketPeriodOptions.IMMEDIATELY,
                  )
                  .map(([value]) => ({
                    id: value,
                    name: `resources.enums.accessTicketPeriodOptions.${value}`,
                  }))}
              />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            (formData?.allowReenter || formData?.multiplicity > 1) &&
            [
              AccessTicketPeriodOptions.ONCE_A_DAY,
              AccessTicketPeriodOptions.ONCE_A_WEEK,
              AccessTicketPeriodOptions.ONCE_A_MONTH,
            ].includes(formData?.ticketDeferOption) && (
              <DateTimeInput
                {...rest}
                source="deferTime"
                type="time"
                format={formatTime}
                parse={(value: string) => (value === '' ? null : value)}
              />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            (formData?.allowReenter || formData?.multiplicity > 1) &&
            formData?.ticketDeferOption ===
              AccessTicketPeriodOptions.ONCE_A_WEEK && (
              <SelectInput
                {...rest}
                isRequired
                source="deferDayOfWeek"
                choices={[
                  ...Object.entries(DayOfWeek).map(([value]) => ({
                    id: value,
                    name: `resources.enums.dayOfWeek.${value}`,
                  })),
                ]}
              />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            (formData?.allowReenter || formData?.multiplicity > 1) &&
            formData?.ticketDeferOption ===
              AccessTicketPeriodOptions.ONCE_A_MONTH && (
              <TextInput {...rest} source="deferDayOfMonth" isRequired />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            (formData?.allowReenter || formData?.multiplicity > 1) &&
            formData?.ticketDeferOption ===
              AccessTicketPeriodOptions.AFTER_DELAY && (
              <TextInput {...rest} source="deferDelay" isRequired />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            (formData?.allowReenter || formData?.multiplicity > 1) &&
            formData?.ticketDeferOption !== AccessTicketPeriodOptions.NONE && (
              <SelectInput
                {...rest}
                isRequired
                source="deferLogic"
                choices={[
                  ...Object.entries(DeferLogicOptions).map(([value]) => ({
                    id: value,
                    name: `resources.enums.DeferLogicOptions.${value}`,
                  })),
                ]}
              />
            )
          }
        </FormDataConsumer>
        <TextInput source="usePriority" />
        <SelectInput
          source="activePeriodStartingFrom"
          choices={Object.entries(AccessActivePeriodStartingOptions).map(
            ([value]) => ({
              id: value,
              name: `resources.enums.activePeriodStartingFrom.${value}`,
            }),
          )}
        />
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData?.activePeriodStartingFrom !==
              AccessActivePeriodStartingOptions.NO_LIMIT && (
              <TextInput source="activePeriod" {...rest} isRequired />
            )
          }
        </FormDataConsumer>
        <BooleanInput source="restrictLicencePlate" />
        <BooleanInput source="allowAddLicencePlate" />
        <BooleanInput source="markNewBookingAsUsed" />
        <SelectInput
          source="ticketUnlockOption"
          choices={Object.entries(AccessTicketPeriodOptions).map(([value]) => ({
            id: value,
            name: `resources.enums.accessTicketPeriodOptions.${value}`,
          }))}
        />
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            [
              AccessTicketPeriodOptions.ONCE_A_DAY,
              AccessTicketPeriodOptions.ONCE_A_WEEK,
              AccessTicketPeriodOptions.ONCE_A_MONTH,
            ].includes(formData?.ticketUnlockOption) && (
              <DateTimeInput
                {...rest}
                isRequired
                source="unlockTime"
                type="time"
                format={formatTime}
                parse={(value: string) => (value === '' ? null : value)}
              />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData?.ticketUnlockOption ===
              AccessTicketPeriodOptions.ONCE_A_WEEK && (
              <SelectInput
                {...rest}
                isRequired
                source="unlockDayOfWeek"
                choices={[
                  ...Object.entries(DayOfWeek).map(([value]) => ({
                    id: value,
                    name: `resources.enums.dayOfWeek.${value}`,
                  })),
                ]}
              />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData?.ticketUnlockOption ===
              AccessTicketPeriodOptions.ONCE_A_MONTH && (
              <TextInput {...rest} source="unlockDayOfMonth" isRequired />
            )
          }
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData?.ticketUnlockOption ===
              AccessTicketPeriodOptions.AFTER_DELAY && (
              <TextInput {...rest} source="unlockDelay" isRequired />
            )
          }
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  )
}
